import { create } from 'zustand';
import { persist } from 'zustand/middleware';
import {
  applySidebarLayout,
  isCurrentSidebarDestinationKey,
  isSidebarPreferenceKey,
  normalizeSidebarLabel,
  normalizeSidebarLayout,
  type SidebarLayout,
} from './sidebarLayout';
import type { NavDomain } from './types';

/**
 * Per-user navigation preferences: which domains are open, which destinations
 * are pinned, and which pages are hidden from the rail.
 *
 * These are UI preferences, not security state — localStorage is the right
 * store. Nothing here widens access: the pinned and hidden lists are applied
 * after the RBAC-filtered tree, so a leftover pin simply stops appearing.
 *
 * Kept out of `uiStore` deliberately: this state is navigation's own, and the
 * shell store is edited by every sprint.
 */
interface NavPreferencesState {
  scopedPreferences: Record<string, ScopedNavPreferences>;
  legacyAvailable: boolean;
  updateScopedPreferences: (scope: string, update: (previous: ScopedNavPreferences) => ScopedNavPreferences) => void;
  importLegacyPreferences: (scope: string, sidebarCollapsed: boolean) => void;
  /** Explicit user choices only. An absent domain falls back to the default. */
  expandedDomains: Record<string, boolean>;
  pinnedIds: string[];
  /** Sidebar destinations the operator chose to keep out of the rail. */
  hiddenIds: string[];
  /** Layout and names are isolated to the authenticated tenant and user. */
  sidebarLayouts: Record<string, SidebarLayout>;
  toggleDomain: (domainId: string) => void;
  setDomainExpanded: (domainId: string, expanded: boolean) => void;
  expandAll: (domainIds: readonly string[]) => void;
  collapseAll: (domainIds: readonly string[]) => void;
  togglePin: (destinationId: string) => void;
  toggleHidden: (destinationId: string) => void;
  moveSidebarDestination: (
    scope: string,
    groups: readonly NavDomain[],
    destinationId: string,
    targetDomainId: string,
    beforeDestinationId?: string,
    completeGroups?: readonly NavDomain[],
  ) => void;
  renameSidebarDestination: (scope: string, destinationId: string, label: string) => void;
  resetSidebarLayout: (scope: string) => void;
}

interface PersistedNavPreferences {
  scopedPreferences: Record<string, ScopedNavPreferences>;
  legacyAvailable: boolean;
  expandedDomains: Record<string, boolean>;
  pinnedIds: string[];
  hiddenIds: string[];
  sidebarLayouts: Record<string, SidebarLayout>;
}

export interface ScopedNavPreferences {
  expandedDomains: Record<string, boolean>;
  configurationDomains: Record<string, boolean>;
  pinnedIds: string[];
  hiddenIds: string[];
  sidebarCollapsed: boolean;
  legacyImported: boolean;
}

export const DEFAULT_SCOPED_NAV_PREFERENCES: ScopedNavPreferences = {
  expandedDomains: {}, configurationDomains: {}, pinnedIds: [], hiddenIds: ['growth-ma'], sidebarCollapsed: false, legacyImported: false,
};

function normalizeScopedPreferences(value: unknown): ScopedNavPreferences {
  const state = preferenceRecord(value);
  const domains = (input: unknown) => Object.fromEntries(Object.entries(preferenceRecord(input)).filter(([id, expanded]) => isSidebarPreferenceKey(id) && typeof expanded === 'boolean')) as Record<string, boolean>;
  return {
    expandedDomains: domains(state.expandedDomains), configurationDomains: domains(state.configurationDomains),
    pinnedIds: preferenceIds(state.pinnedIds), hiddenIds: preferenceIds(state.hiddenIds, DEFAULT_HIDDEN_IDS),
    sidebarCollapsed: state.sidebarCollapsed === true, legacyImported: state.legacyImported === true,
  };
}

const OBSOLETE_DEFAULT_LABELS: Readonly<Record<string, string>> = {
  'executive-summary': 'Executive Summary',
  'm365-workspace': 'My Day',
};

const NAV_PREFERENCES_VERSION = 7;

function preferenceRecord(value: unknown): Record<string, unknown> {
  return typeof value === 'object' && value !== null && !Array.isArray(value)
    ? value as Record<string, unknown>
    : {};
}

function preferenceIds(value: unknown, fallback: readonly string[] = []): string[] {
  return Array.isArray(value) ? [...new Set(value.filter(isCurrentSidebarDestinationKey))] : [...fallback];
}

/** Keep unavailable pages in their saved gaps while capturing the visible order. */
function mergeSidebarSection(
  previousIds: readonly string[],
  currentIds: readonly string[],
  visibleIds: ReadonlySet<string>,
): string[] {
  const current = new Set(currentIds);
  const merged: string[] = [];
  const trailing: string[] = [];
  let lastVisibleIndex = -1;
  previousIds.forEach((id, index) => { if (visibleIds.has(id)) lastVisibleIndex = index; });
  let visibleIndex = 0;
  for (const [index, id] of previousIds.entries()) {
    if (!visibleIds.has(id)) {
      // End anchors remain at the end when an insertion adds visible slots.
      (lastVisibleIndex >= 0 && index > lastVisibleIndex ? trailing : merged).push(id);
    } else if (current.has(id)) merged.push(currentIds[visibleIndex++]);
  }
  return [...merged, ...currentIds.slice(visibleIndex), ...trailing];
}

/** Also used by merge, since current-version storage bypasses Zustand's migrate. */
function normalizePreferences(value: unknown, version = NAV_PREFERENCES_VERSION): PersistedNavPreferences {
  const state = preferenceRecord(value);
  const pinnedIds = [...new Set(preferenceIds(state.pinnedIds).map((id) =>
    id === 'command-center' ? 'executive-summary' : id,
  ))];
  const expandedDomains = Object.fromEntries(Object.entries(preferenceRecord(state.expandedDomains))
    .filter(([id, expanded]) => isSidebarPreferenceKey(id) && typeof expanded === 'boolean')) as Record<string, boolean>;
  if (version < 3 && 'overview' in expandedDomains) {
    expandedDomains['executive-management'] = expandedDomains.overview;
    delete expandedDomains.overview;
  }
  return {
    scopedPreferences: Object.fromEntries(Object.entries(preferenceRecord(state.scopedPreferences)).filter(([scope]) => isSidebarPreferenceKey(scope)).map(([scope, preferences]) => [scope, normalizeScopedPreferences(preferences)])),
    legacyAvailable: state.legacyAvailable === true || (version < 6 && ['expandedDomains', 'pinnedIds', 'hiddenIds'].some((key) => Object.hasOwn(state, key))),
    expandedDomains,
    pinnedIds,
    hiddenIds: version < 2 ? [...DEFAULT_HIDDEN_IDS] : preferenceIds(state.hiddenIds, DEFAULT_HIDDEN_IDS),
    sidebarLayouts: Object.fromEntries(Object.entries(preferenceRecord(state.sidebarLayouts))
      .filter(([scope]) => isSidebarPreferenceKey(scope))
      .map(([scope, layout]) => {
        const normalized = normalizeSidebarLayout(layout);
        // Only upgrade saved copies of the old defaults during migration.
        // Operators may still choose either phrase as a personal name later.
        if (version < 5) {
          normalized.labels = Object.fromEntries(Object.entries(normalized.labels)
            .filter(([id, label]) => label !== OBSOLETE_DEFAULT_LABELS[id]));
        }
        // Retire the former default only when upgrading an older saved layout.
        // Version 7 and later names remain deliberate personal choices.
        if (version < 7 && normalized.labels.debt === 'Debt') delete normalized.labels.debt;
        return [scope, normalized];
      })),
  };
}

/**
 * Domains open on a first visit. Executive Management only: nine scannable
 * headers with the landing domain open is the density the command centre
 * wants. The domain containing the active route is expanded on top of this
 * by the tree itself.
 */
export const DEFAULT_EXPANDED_DOMAINS: readonly string[] = ['executive-management'];

/**
 * Pages hidden on a first visit. Growth & M&A stays routeable and can be
 * shown again from the account Settings menu — it is not deleted.
 */
export const DEFAULT_HIDDEN_IDS: readonly string[] = ['growth-ma'];

export const useNavPreferences = create<NavPreferencesState>()(
  persist(
    (set) => ({
      scopedPreferences: {},
      legacyAvailable: false,
      updateScopedPreferences: (scope, update) => set((state) => isSidebarPreferenceKey(scope) ? { scopedPreferences: { ...state.scopedPreferences, [scope]: normalizeScopedPreferences(update(state.scopedPreferences[scope] ?? DEFAULT_SCOPED_NAV_PREFERENCES)) } } : state),
      importLegacyPreferences: (scope, sidebarCollapsed) => set((state) => {
        if (!isSidebarPreferenceKey(scope) || !state.legacyAvailable || state.scopedPreferences[scope]?.legacyImported) return state;
        return { scopedPreferences: { ...state.scopedPreferences, [scope]: normalizeScopedPreferences({ expandedDomains: state.expandedDomains, pinnedIds: state.pinnedIds, hiddenIds: state.hiddenIds, sidebarCollapsed, legacyImported: true }) } };
      }),
      expandedDomains: {},
      pinnedIds: [],
      hiddenIds: [...DEFAULT_HIDDEN_IDS],
      sidebarLayouts: {},

      toggleDomain: (domainId) =>
        set((state) => ({
          expandedDomains: {
            ...state.expandedDomains,
            [domainId]: !(state.expandedDomains[domainId] ?? isExpandedByDefault(domainId)),
          },
        })),

      setDomainExpanded: (domainId, expanded) =>
        set((state) => ({
          expandedDomains: { ...state.expandedDomains, [domainId]: expanded },
        })),

      expandAll: (domainIds) =>
        set((state) => ({
          expandedDomains: {
            ...state.expandedDomains,
            ...Object.fromEntries(domainIds.map((id) => [id, true])),
          },
        })),

      collapseAll: (domainIds) =>
        set((state) => ({
          expandedDomains: {
            ...state.expandedDomains,
            ...Object.fromEntries(domainIds.map((id) => [id, false])),
          },
        })),

      togglePin: (destinationId) =>
        set((state) => isCurrentSidebarDestinationKey(destinationId) ? ({
          pinnedIds: state.pinnedIds.includes(destinationId)
            ? state.pinnedIds.filter((id) => id !== destinationId)
            : [...state.pinnedIds, destinationId],
        }) : state),

      toggleHidden: (destinationId) =>
        set((state) => {
          if (!isCurrentSidebarDestinationKey(destinationId)) return state;
          const hiddenIds = state.hiddenIds ?? [];
          return {
            hiddenIds: hiddenIds.includes(destinationId)
              ? hiddenIds.filter((id) => id !== destinationId)
              : [...hiddenIds, destinationId],
          };
        }),

      moveSidebarDestination: (scope, groups, destinationId, targetDomainId, beforeDestinationId, completeGroups = groups) =>
        set((state) => {
          if (!isSidebarPreferenceKey(scope)) return state;
          const previous = normalizeSidebarLayout(state.sidebarLayouts[scope]);
          const current = applySidebarLayout(groups, previous);
          const target = current.find((group) => group.id === targetDomainId);
          const source = current.find((group) => group.items.some((item) => item.id === destinationId));
          if (!target || !source) return state;
          if (beforeDestinationId === destinationId) return state;
          if (beforeDestinationId && !target.items.some((item) => item.id === beforeDestinationId)) return state;

          const rememberedSource = Object.entries(previous.sections)
            .find(([, ids]) => ids.includes(destinationId))?.[0];
          if (source.id === target.id && (!rememberedSource || rememberedSource === target.id)) {
            const visibleOrder = target.items.map((item) => item.id);
            const movedOrder = visibleOrder.filter((id) => id !== destinationId);
            const insertionIndex = beforeDestinationId ? movedOrder.indexOf(beforeDestinationId) : movedOrder.length;
            movedOrder.splice(insertionIndex, 0, destinationId);
            if (movedOrder.every((id, index) => id === visibleOrder[index])) return state;
          }

          const visibleIds = new Set(current.flatMap((group) => group.items.map((item) => item.id)));
          // Use the complete registry only for ordering anchors, never to authorize
          // an edit or render a page. First edits must retain pages hidden by the
          // operator, permissions, or a filtered view before a saved order exists.
          const complete = applySidebarLayout(completeGroups, previous);
          const knownIds = new Set(completeGroups.flatMap((group) => group.items.map((item) => item.id)));
          const anchors = new Map(complete.map((group) => [group.id, mergeSidebarSection(
            previous.sections[group.id] ?? [], group.items.map((item) => item.id), knownIds,
          )]));
          const availableSections = new Set(current.map((group) => group.id));
          // A role may hide the saved target section while the page itself remains
          // permitted. Its temporary fallback must not overwrite that placement.
          const reserved = new Set(Object.entries(previous.sections)
            .filter(([domainId]) => !availableSections.has(domainId))
            .flatMap(([, ids]) => ids.filter((id) => id !== destinationId)));
          const sections = { ...previous.sections };
          const beforeIndex = beforeDestinationId ? target.items.findIndex((item) => item.id === beforeDestinationId) : -1;
          const nextId = beforeIndex >= 0
            ? target.items.slice(beforeIndex).find((item) => item.id !== destinationId && !reserved.has(item.id))?.id
            : undefined;
          // Only touched sections need a snapshot. Unrelated hidden/denied pages
          // and their anchors remain exactly where the operator left them.
          for (const group of source.id === target.id ? [source] : [source, target]) {
            const currentIds = group.items.map((item) => item.id)
              .filter((id) => id !== destinationId && !reserved.has(id));
            if (group.id === targetDomainId) {
              currentIds.splice(nextId ? currentIds.indexOf(nextId) : currentIds.length, 0, destinationId);
            }
            // Reorder the visible slots, preserving the hidden anchors between
            // them. Inserting into the full list directly would shift those gaps.
            sections[group.id] = mergeSidebarSection(anchors.get(group.id) ?? previous.sections[group.id] ?? [], currentIds, visibleIds);
          }
          for (const id of Object.keys(sections)) {
            if (id !== targetDomainId) sections[id] = sections[id].filter((id) => id !== destinationId);
          }
          return {
            sidebarLayouts: { ...state.sidebarLayouts, [scope]: { sections, labels: previous.labels } },
            scopedPreferences: { ...state.scopedPreferences, [scope]: { ...(state.scopedPreferences[scope] ?? DEFAULT_SCOPED_NAV_PREFERENCES), expandedDomains: { ...(state.scopedPreferences[scope]?.expandedDomains ?? {}), [targetDomainId]: true } } },
          };
        }),

      renameSidebarDestination: (scope, destinationId, rawLabel) =>
        set((state) => {
          if (!isSidebarPreferenceKey(scope) || !isCurrentSidebarDestinationKey(destinationId)) return state;
          const previous = normalizeSidebarLayout(state.sidebarLayouts[scope]);
          const labels = { ...previous.labels };
          const label = normalizeSidebarLabel(rawLabel);
          if (label) labels[destinationId] = label;
          else delete labels[destinationId];
          return { sidebarLayouts: { ...state.sidebarLayouts, [scope]: { ...previous, labels } } };
        }),

      resetSidebarLayout: (scope) =>
        set((state) => {
          const sidebarLayouts = { ...state.sidebarLayouts };
          delete sidebarLayouts[scope];
          return { sidebarLayouts };
        }),
    }),
    {
      name: 'helios.nav',
      version: NAV_PREFERENCES_VERSION,
      partialize: (state) => ({
        scopedPreferences: state.scopedPreferences,
        legacyAvailable: state.legacyAvailable,
        expandedDomains: state.expandedDomains,
        pinnedIds: state.pinnedIds,
        hiddenIds: state.hiddenIds,
        sidebarLayouts: state.sidebarLayouts,
      }),
      migrate: (persisted, version) => normalizePreferences(persisted, version),
      merge: (persisted, current) => ({ ...current, ...normalizePreferences(persisted) }),
    },
  ),
);

export function isExpandedByDefault(domainId: string): boolean {
  return DEFAULT_EXPANDED_DOMAINS.includes(domainId);
}

/** Resolve the rendered open/closed state for a domain. */
export function isDomainExpanded(
  expandedDomains: Record<string, boolean>,
  domainId: string,
): boolean {
  return expandedDomains[domainId] ?? isExpandedByDefault(domainId);
}

export function isNavDestinationHidden(
  hiddenIds: readonly string[],
  destinationId: string,
): boolean {
  return hiddenIds.includes(destinationId);
}
